import React, {useEffect, useState} from "react";

export interface BeforeInstallPromptEvent extends Event {
    /**
     * Returns an array of DOMString items containing the platforms on which the event was dispatched.
     * This is provided for user agents that want to present a choice of versions to the user such as,
     * for example, "web" or "play" which would allow the user to chose between a web version or
     * an Android version.
     */
    readonly platforms: Array<string>;

    /**
     * Returns a Promise that resolves to a DOMString containing either "accepted" or "dismissed".
     */
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed',
        platform: string
    }>;

    /**
     * Allows a developer to show the install prompt at a time of their own choosing.
     * This method returns a Promise.
     */
    prompt(): Promise<void>;
}

interface PwaContextProviderState {
    deferredPrompt: BeforeInstallPromptEvent | null;
    setDeferredPrompt: (deferredPrompt: BeforeInstallPromptEvent | null) => void;
}

interface PwaContextInterface extends PwaContextProviderState {
}

export const PwaContext = React.createContext<PwaContextInterface>({
    deferredPrompt: null,
    setDeferredPrompt: (deferredPrompt: BeforeInstallPromptEvent | null) => {
    }
});

export const PwaContextProvider = (props: { children: React.ReactNode }) => {

    useEffect(() => {
        const handleBeforeInstallPromptEvent = (e: Event) => {
            // Prevents the default mini-infobar or install dialog from appearing on mobile
            e.preventDefault();
            // Save the event because you'll need to trigger it later.
            setPwaState({...pwaState, deferredPrompt: e as BeforeInstallPromptEvent});
        }

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPromptEvent);

        return () => window.removeEventListener("beforeinstallprompt", handleBeforeInstallPromptEvent);
    }, []);
    
    const setDeferredPrompt = (deferredPrompt: BeforeInstallPromptEvent | null) => {
        setPwaState({...pwaState, deferredPrompt});
    }

    const [pwaState, setPwaState] = useState<PwaContextInterface>({
        deferredPrompt: null,
        setDeferredPrompt: setDeferredPrompt
    });

    return (
        <PwaContext.Provider
            value={{...pwaState}}>
            {props.children}
        </PwaContext.Provider>
    )
}